/**
 * AnalyzeThisPopover - Confirmation popover for "Analyze in Chat" actions
 *
 * Replaces the auto-send-to-chat pattern with a user-confirmation flow.
 * Shows a floating popover near the clicked element with an editable query,
 * allowing the user to review, modify, and confirm before sending to chat.
 *
 * Rendered via a portal so it floats above all other content.
 *
 * Usage (via chatStore):
 *   chatStore.showAnalyzePrompt(query, context, anchorRect)
 *   // Popover appears; user confirms or cancels
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AnalyzeThisPopoverProps {
  /** Pre-composed query to send to chat */
  query: string;
  /** Description of what was clicked (shown as context label) */
  context?: string;
  /** Position of the anchor element — popover appears near this rect */
  anchorRect: DOMRect;
  /** Called when the user confirms — receives the (possibly edited) query */
  onConfirm: (finalQuery: string) => void;
  /** Called when the user cancels */
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Positioning helper
// ---------------------------------------------------------------------------

const POPOVER_GAP = 8;

function computePosition(anchorRect: DOMRect): { top: number; left: number; width: number; placement: 'above' | 'below' } {
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const isMobile = viewportWidth < 640;

  // On mobile: bottom sheet style. On desktop: floating popover.
  const popoverWidth = isMobile ? viewportWidth - 24 : Math.min(360, viewportWidth - 24);

  // Determine vertical placement: prefer below, but flip above if not enough space
  const spaceBelow = viewportHeight - anchorRect.bottom;
  const spaceAbove = anchorRect.top;
  const estimatedHeight = 200;

  let placement: 'above' | 'below';
  let top: number;

  if (isMobile) {
    // On mobile, center vertically or place at bottom
    placement = 'below';
    top = Math.min(anchorRect.bottom + POPOVER_GAP, viewportHeight - estimatedHeight - 12);
  } else {
    placement = spaceBelow >= estimatedHeight + POPOVER_GAP ? 'below' : spaceAbove >= estimatedHeight + POPOVER_GAP ? 'above' : 'below';
    top = placement === 'below'
      ? anchorRect.bottom + POPOVER_GAP
      : anchorRect.top - estimatedHeight - POPOVER_GAP;
  }

  // Horizontal: center on anchor, but clamp to viewport
  let left = isMobile ? 12 : anchorRect.left + anchorRect.width / 2 - popoverWidth / 2;
  left = Math.max(12, Math.min(left, viewportWidth - popoverWidth - 12));

  return { top: Math.max(12, top), left, width: popoverWidth, placement };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AnalyzeThisPopover({
  query,
  context,
  anchorRect,
  onConfirm,
  onCancel,
}: AnalyzeThisPopoverProps) {
  const [editedQuery, setEditedQuery] = useState(query);
  const popoverRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus and select textarea on mount
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.focus();
      ta.setSelectionRange(ta.value.length, ta.value.length);
    }
  }, []);

  // Auto-resize textarea to content
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    }
  }, [editedQuery]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onCancel();
      }
    }
    // Use a short delay so the originating click doesn't immediately close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCancel]);

  const handleConfirm = useCallback(() => {
    const trimmed = editedQuery.trim();
    if (trimmed) {
      onConfirm(trimmed);
    }
  }, [editedQuery, onConfirm]);

  // Submit on Cmd/Ctrl + Enter
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleConfirm();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleConfirm]);

  const { top, left, width: popoverWidth } = computePosition(anchorRect);

  const popover = (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Analyze in Chat"
      className="fixed z-[100] animate-fade-in"
      style={{ top, left, width: popoverWidth }}
    >
      <div className="bg-bg-0 border border-border rounded-xl shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <MessageSquare size={16} className="text-monarch-gold" />
            <span className="text-sm font-semibold text-text">
              Analyze in Chat?
            </span>
          </div>
          <button
            onClick={onCancel}
            className="p-1 text-text-3 hover:text-text transition-colors"
            aria-label="Cancel"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-3">
          {/* Context label */}
          {context && (
            <p className="text-xs text-text-3 leading-relaxed">
              {context}
            </p>
          )}

          {/* Editable query */}
          <textarea
            ref={textareaRef}
            value={editedQuery}
            onChange={(e) => setEditedQuery(e.target.value)}
            rows={2}
            className="
              w-full resize-none
              bg-bg-1 border border-border rounded-xl
              px-3 py-2 text-sm text-text
              placeholder:text-text-3
              focus:outline-none focus:border-monarch-gold
              transition-colors
            "
            placeholder="Enter your question..."
          />

          {/* Actions */}
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-text-3">
              {navigator.platform.includes('Mac') ? '\u2318' : 'Ctrl'}+Enter to send
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={onCancel}
                className="
                  px-3 py-1.5 text-xs text-text-3
                  hover:text-text transition-colors
                "
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!editedQuery.trim()}
                className="
                  px-4 py-1.5 text-xs font-medium text-white
                  bg-monarch-gold hover:bg-monarch-gold-dark
                  disabled:opacity-40 disabled:cursor-not-allowed
                  rounded-xl transition-colors
                "
              >
                Send to Chat
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(popover, document.body);
}
